import type { BookType } from "xlsx"
import { utils as xlsxUtils, write as xlsxWrite, writeFile as xlsxWriteFile } from "xlsx"
import * as dt from "date-fns"
import { EnergyEstimationMode, Unit } from "#imports"
import type { NoteTagSchema, PastillaSchemaShort, SiteSchemaShort } from "#imports"
import { formatSiteNames, sortSitesByName } from "~/utils/business"

function formatSiteValues(
  sites: SiteSchemaShort[],
  getValue: (site: SiteSchemaShort) => string | number | null | undefined,
) {
  const values = R.pipe(
    sortSitesByName(sites),
    R.map(getValue),
    R.filter(R.isNonNullish),
    R.unique(),
  )
  return values.length === 1 ? values[0] : values.join(", ")
}

/**
 * A function that create convert array of object into an excel file and make the browser download it.
 * @param {string} fileName - The name that the excel file should take.
 * @param {BookType} type - The extension of the file (xsl,xslx,csv,...)
 * @param {Array<any>} data -  The data to convert to excel
 */
export function dataToExcel(fileName: string, type: BookType, data: any[]): void {
  const now = dt.format(new Date(), "MM/dd-HH:mm")
  const wb = xlsxUtils.book_new()
  const file = xlsxUtils.json_to_sheet(data)
  xlsxUtils.book_append_sheet(wb, file, fileName)
  xlsxWrite(wb, { bookType: type, type: "binary" })
  xlsxWriteFile(wb, `${fileName}-${now}.${type}`)
}

interface DataMapper {
  model: string
  field?: string
  headerName: string
  format?: (value: any) => any
}

/** Minimal i18n surface used by excel export — decoupled from vue-i18n internals. */
export interface ExcelI18n {
  t(key: string): string
  n(value: number, format?: string): string
}

export function formatDataToExcel(
  filterObject: "Site" | "Contract" | "Invoice" | "Event" | "Note",
  items: any[],
  i18n: ExcelI18n,
): any[] {
  const sepArray = ", "

  if (filterObject === "Invoice") {
    items = items.map((invoice) => computeTotalLinesOnInvoice(invoice))
  }

  const formatters = {
    date: toISODate,
    fullDate: (i: string | Date) => toISODate(i, true),
    unitValue: (fromUnit: Unit, toUnit?: Unit) => (value: number | null | undefined) => {
      const convRate = toUnit ? getUnitConversionRate(fromUnit, toUnit) : 1
      return typeof value === "number" ? i18n.n(value * convRate, toUnit || fromUnit) : ""
    },
    sites: (s: { name: string; uuid: string }[]) => {
      if (!Array.isArray(s)) return ""
      return s.map((site) => site.name).join(sepArray)
    },
    contract_type: deslugContractType,
    contract_status: (values: string[]) => {
      if (!Array.isArray(values)) return ""
      return values.map((value) => i18n.t(`site.contract_status.${value}`)).join(sepArray)
    },
    site_status: (value: string) => i18n.t(`site.status.${value}`),
    dp_codes: (values: string[]) => (Array.isArray(values) ? values.join(sepArray) : ""),
    availability_status: (values: PastillaSchemaShort[]) => {
      if (!Array.isArray(values)) return ""
      const result = values.find((v) => v.name === "availability")
      return result ? i18n.t(`pastilla.status.${result.status}`) : ""
    },
    availability_status_change: (values: PastillaSchemaShort[]) => {
      if (!Array.isArray(values)) return ""
      const result = values.find((v) => v.name === "availability")
      return result ? toISODate(result.last_status_change, true) : ""
    },
    upperCase: (value: string) => value.toUpperCase(),
    duration: (item: any) => {
      return dt.formatDuration(dt.intervalToDuration({ start: item.start, end: item.end }))
    },
    tags: (values: NoteTagSchema[]) => {
      if (!Array.isArray(values)) return ""
      const result = values.map((e) => e.name)
      return result.join(sepArray)
    },
  }

  const dataMapperContract: DataMapper[] = [
    {
      model: "Contract",
      field: "sites",
      headerName: i18n.t("contract.header.site_name"),
      format: (sites) => formatSiteNames(sites, { truncate: false }),
    },
    {
      model: "Contract",
      field: "sites",
      headerName: i18n.t("title.asset_owner"),
      format: (sites) => formatSiteValues(sites, (site) => site.assetowner.name.toUpperCase()),
    },
    {
      model: "Contract",
      field: "purchase_order",
      headerName: i18n.t("contract.header.id_purchase"),
    },
    {
      model: "Contract",
      field: "sites",
      headerName: i18n.t("contract.header.techno"),
      format: (sites) =>
        formatSiteValues(sites, (site) => i18n.t(`machine_type.${site.machine_type}`)),
    },
    {
      model: "Contract",
      field: "start",
      headerName: i18n.t("contract.header.start"),
      format: formatters.date,
    },
    {
      model: "Contract",
      field: "end",
      headerName: i18n.t("contract.header.end"),
      format: formatters.date,
    },
    {
      model: "Contract",
      field: "sites",
      headerName: i18n.t("contract.header.site_capacity"),
      format: (sites) => formatSiteValues(sites, (site) => site.capacity),
    },
    {
      model: "Contract",
      field: "price_mechanism",
      headerName: i18n.t("contract.header.price_mecha"),
      format: (value) => i18n.t(`site.contract_status.${value}`),
    },
    {
      model: "Contract",
      field: "type.name",
      headerName: i18n.t("contract.header.type"),
      format: formatters.contract_type,
    },
    { model: "Contract", field: "buyer.name", headerName: i18n.t("contract.header.buyer") },
    { model: "Contract", field: "seller.name", headerName: i18n.t("contract.header.seller") },
    {
      model: "Contract",
      field: "invoice_source",
      headerName: i18n.t("contract.header.invoice_source"),
      format: (value) => i18n.t(`invoice.source.${value}`),
    },
    {
      model: "Contract",
      field: "current_status",
      headerName: i18n.t("contract.header.current_status"),
      format: (value) => i18n.t(`contract.status.${value}`),
    },
    {
      model: "Contract",
      field: "sites",
      headerName: i18n.t("contract.header.p50"),
      format: (sites) => formatSiteValues(sites, (site) => site.p50),
    },
    { model: "Contract", field: "tarif", headerName: i18n.t("contract.header.tarif") },
    { model: "Contract", field: "fix_price", headerName: i18n.t("contract.header.fix_price") },
    {
      model: "Contract",
      field: "management_fee",
      headerName: i18n.t("contract.header.management_fee"),
    },
  ]

  const dataMapperSite: DataMapper[] = [
    {
      model: "Site",
      field: "name",
      headerName: i18n.t("contract.header.site_name"),
    },
    {
      model: "Site",
      field: "machine_type",
      headerName: i18n.t("contract.header.techno"),
      format: (value) => i18n.t(`machine_type.${value}`),
    },
    { model: "Site", field: "region.name", headerName: i18n.t("contract.header.region") },
    { model: "Site", field: "capacity", headerName: i18n.t("contract.header.site_capacity") },
    { model: "Site", field: "p50", headerName: i18n.t("contract.header.p50") },
    {
      model: "Site",
      field: "commissioning",
      headerName: i18n.t("contract.header.commissioning"),
      format: formatters.date,
    },
    { model: "Site", field: "assetowner.name", headerName: i18n.t("contract.header.spv") },
    { model: "Site", field: "is_external", headerName: i18n.t("company.header.external") },
    { model: "Site", field: "latitude", headerName: i18n.t("contract.header.latitude") },
    { model: "Site", field: "longitude", headerName: i18n.t("contract.header.longitude") },
    {
      model: "Site",
      field: "contract_status",
      headerName: i18n.t("contract.header.current_status"),
      format: formatters.contract_status,
    },
    {
      model: "Site",
      field: "status",
      headerName: i18n.t("contract.header.site_status"),
      format: formatters.site_status,
    },
    {
      model: "Site",
      field: "portfolio.name",
      headerName: i18n.t("contract.header.portfolio_name"),
    },
    { model: "Site", field: "external_id", headerName: i18n.t("contract.header.external_id") },
    {
      model: "Site",
      field: "dp_codes",
      headerName: i18n.t("contract.header.dp_codes"),
      format: formatters.dp_codes,
    },
    {
      model: "Site",
      field: "dp_references",
      headerName: i18n.t("contract.header.dp_references"),
      format: formatters.dp_codes,
    },
    {
      model: "Site",
      field: "cached_pastilla_values",
      headerName: i18n.t("contract.header.availability_status"),
      format: formatters.availability_status,
    },
    {
      model: "Site",
      field: "cached_pastilla_values",
      headerName: i18n.t("contract.header.availability_status_change"),
      format: formatters.availability_status_change,
    },
  ]

  const dataMapperInvoice: DataMapper[] = [
    {
      model: "Invoice",
      field: "sites",
      headerName: i18n.t("invoice.header.site_name"),
      format: (sites) => formatSiteNames(sites, { truncate: false }),
    },
    {
      model: "Invoice",
      field: "sites",
      headerName: i18n.t("invoice.header.asset_owner"),
      format: (sites) => formatSiteValues(sites, (site) => site.assetowner.name.toUpperCase()),
    },
    {
      model: "Invoice",
      field: "sites",
      headerName: i18n.t("invoice.header.external_id"),
      format: (sites) => formatSiteValues(sites, (site) => site.external_id),
    },
    {
      model: "Invoice",
      field: "sites",
      headerName: i18n.t("contract.header.portfolio_name"),
      format: (sites) => formatSiteValues(sites, (site) => site.portfolio?.name),
    },
    { model: "Invoice", field: "contract.type.name", headerName: i18n.t("title.contract_type") },
    {
      model: "Invoice",
      field: "contract.invoice_submit_channel",
      headerName: i18n.t("invoice.header.submit_channel"),
      format: (value) => i18n.t(`contract.invoice_submit_channel.${value}`),
    },
    {
      model: "Invoice",
      field: "contract.buyer.name",
      headerName: i18n.t("contract.header.buyer"),
    },
    {
      model: "Invoice",
      field: "contract.purchase_order",
      headerName: i18n.t("invoice.header.contract_id"),
    },
    { model: "Invoice", field: "start", headerName: i18n.t("time.start"), format: formatters.date },
    { model: "Invoice", field: "end", headerName: i18n.t("time.end"), format: formatters.date },
    {
      model: "Invoice",
      headerName: i18n.t("time.period"),
      format: (item) => {
        const fmt = "yyyy-MM-dd"
        const start = dt.format(toDateTZ(item.start), fmt)
        const end = dt.format(toDateTZ(item.end), fmt)
        return `${start} / ${end}`
      },
    },
    {
      model: "Invoice",
      field: "status",
      headerName: i18n.t("invoice.header.status"),
      format: (value) => i18n.t(`invoice.status.${value}`),
    },
    {
      model: "Invoice",
      field: "publication_id",
      headerName: i18n.t("invoice.header.publication_id"),
    },
    {
      model: "Invoice",
      headerName: i18n.t("title.kind"),
      format: (item) => i18n.t(`invoice.kind.${computeInvoiceKind(item)}`),
    },
    {
      model: "Invoice",
      field: "bestof_total_line.price",
      headerName: i18n.t("invoice.header.average_price"),
      format: Number,
    },
    {
      model: "Invoice",
      field: "bestof_total_line.amount",
      headerName: i18n.t("invoice.header.amount"),
      format: Number,
    },
    {
      model: "Invoice",
      field: "bestof_total_line.quantity",
      headerName: i18n.t("invoice.header.quantity"),
      format: Number,
    },
    {
      model: "Invoice",
      field: "total_line.price",
      headerName: i18n.t("invoice.header.average_price_computed"),
      format: Number,
    },
    {
      model: "Invoice",
      field: "total_line.amount",
      headerName: i18n.t("invoice.header.amount_computed"),
    },
    {
      model: "Invoice",
      field: "total_line.quantity",
      headerName: i18n.t("invoice.header.quantity_computed"),
      format: Number,
    },
    {
      model: "Invoice",
      field: "external_status",
      headerName: i18n.t("title.external_status"),
      format: (value) =>
        R.isNonNullish(value) ? i18n.t(`invoice.external_status.${value}`) : undefined,
    },
    {
      model: "Invoice",
      field: "computation_date",
      headerName: i18n.t("invoice.header.computation_date"),
      format: formatters.date,
    },
    {
      model: "Invoice",
      field: "publication_date",
      headerName: i18n.t("invoice.header.submission_date"),
      format: formatters.date,
    },
    {
      model: "Invoice",
      field: "payment_date",
      headerName: i18n.t("invoice.header.payment_date"),
      format: formatters.date,
    },
    {
      model: "Invoice",
      field: "is_accounted",
      headerName: i18n.t("invoice.title.accounted"),
    },
  ]

  const dataMapperEvent: DataMapper[] = [
    { model: "Event", field: "site.external_id", headerName: i18n.t("calendar.event.external_id") },
    { model: "Event", field: "site.name", headerName: i18n.t("title.name") },
    { model: "Event", field: "site.portfolio.name", headerName: i18n.t("title.portfolio") },
    { model: "Event", field: "site.assetowner.name", headerName: i18n.t("title.asset_owner") },
    {
      model: "Event",
      field: "start",
      headerName: i18n.t("time.unavailability_start"),
      format: formatters.fullDate,
    },
    {
      model: "Event",
      field: "end",
      headerName: i18n.t("time.unavailability_end"),
      format: formatters.fullDate,
    },
    { model: "Event", headerName: i18n.t("time.duration"), format: formatters.duration },
    {
      model: "Event",
      field: "event_type",
      headerName: i18n.t("title.type"),
      format: (value) => i18n.t(`event.type.${value}`),
    },
    // Losses are estimated per EnergyEstimationMode: one column pair per mode,
    // prefixed with the mode name.
    ...EnergyEstimationMode.options.flatMap((mode): DataMapper[] => [
      {
        model: "Event",
        field: `losses.${mode}.revenue_loss`,
        headerName: `${i18n.t(`event.energy_estimation_mode.${mode}`)} - ${i18n.t("calendar.event.revenue_losses")}`,
        format: formatters.unitValue(Unit.enum.eur),
      },
      {
        model: "Event",
        field: `losses.${mode}.energy_loss`,
        headerName: `${i18n.t(`event.energy_estimation_mode.${mode}`)} - ${i18n.t("calendar.event.energy_losses")}`,
        format: formatters.unitValue(Unit.enum.MWh),
      },
    ]),
    {
      model: "Event",
      field: "description",
      headerName: i18n.t("title.description"),
    },
    {
      model: "Event",
      field: "is_external",
      headerName: i18n.t("title.is_external"),
    },
    {
      model: "Event",
      field: "cause",
      headerName: i18n.t("title.cause"),
      format: (value) => (R.isNonNullish(value) ? i18n.t(`event.cause.${value}`) : undefined),
    },
  ]

  const dataMapperNote: DataMapper[] = [
    {
      model: "Note",
      field: "target.target_model",
      headerName: i18n.t("note.header.target_model"),
      format: (value) => i18n.t(`object.${value}`),
    },
    { model: "Note", field: "target.target_name", headerName: i18n.t("note.header.target_name") },
    {
      model: "Note",
      field: "start",
      headerName: i18n.t("time.start"),
      format: formatters.date,
    },
    {
      model: "Note",
      field: "end",
      headerName: i18n.t("time.end"),
      format: formatters.date,
    },
    { model: "Note", field: "body", headerName: i18n.t("note.header.body") },
    {
      model: "Note",
      field: "level",
      headerName: i18n.t("note.header.level"),
      format: (value) => i18n.t(`note.level.${value}`),
    },
    { model: "Note", field: "pinned", headerName: i18n.t("note.header.pinned") },
    {
      model: "Note",
      field: "tags",
      headerName: i18n.t("note.header.tags"),
      format: formatters.tags,
    },
    { model: "Note", field: "archived", headerName: i18n.t("note.header.archived") },
    { model: "Note", field: "created_by", headerName: i18n.t("note.header.created_by") },
    {
      model: "Note",
      field: "creation_date",
      headerName: i18n.t("note.header.creation_date"),
      format: formatters.date,
    },
    { model: "Note", field: "updated_by", headerName: i18n.t("note.header.updated_by") },
    {
      model: "Note",
      field: "update_date",
      headerName: i18n.t("note.header.update_date"),
      format: formatters.date,
    },
  ]

  const dataMapper: DataMapper[] = [
    ...dataMapperContract,
    ...dataMapperSite,
    ...dataMapperInvoice,
    ...dataMapperEvent,
    ...dataMapperNote,
  ]
  const excelData: any[] = []
  const filteredDataMapper: DataMapper[] = dataMapper.filter((e) => e.model === filterObject)

  items.forEach((data) => {
    const row: any = {}
    filteredDataMapper.forEach((el: DataMapper) => {
      // Access nested property by path - if no field is passed we use whole item
      let value: any = el.field
        ? el.field.split(".").reduce((obj: any, key: string) => obj?.[key], data)
        : data

      if (R.isString(value) || el.format) {
        const fnFmt = el.format || ((value) => value)
        value = fnFmt(value)
      }

      row[el.headerName] = value
    })
    excelData.push(row)
  })
  return excelData
}
